import React, { useState } from 'react';
import { formatValue } from '../../utils/formatters';

const FormsSection = ({ forms, onViewForm, title }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    if (!forms || !Array.isArray(forms) || forms.length === 0) {
        return null;
    }

    const visibleForms = isExpanded ? forms : forms.slice(0, 3);
    const hasMore = forms.length > 3;
    const sectionTitle = title || 'Forms';

    // Calculate summary stats for a form
    const getFormStats = (form) => {
        const fields = form.fields || [];
        const totalFields = fields.length;
        const requiredFields = fields.filter(f => f.required).length;
        const visibleFields = fields.filter(f => f.type !== 'hidden').length;
        const fieldTypes = {};
        fields.forEach(f => {
            const type = f.type || 'other';
            fieldTypes[type] = (fieldTypes[type] || 0) + 1;
        });

        return {
            totalFields,
            requiredFields,
            visibleFields,
            hiddenFields: totalFields - visibleFields,
            fieldTypes
        };
    };

    return (
        <div style={{ marginBottom: '30px' }}>
            <h4 style={{ marginBottom: '15px', color: '#e0e0e0', fontWeight: '600' }}>
                {sectionTitle} ({forms.length} {forms.length === 1 ? 'form' : 'forms'})
            </h4>
            <div style={{
                backgroundColor: '#2d2d2d',
                border: '1px solid #404040',
                borderRadius: '6px',
                padding: '20px'
            }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {visibleForms.map((form, index) => {
                        const stats = getFormStats(form);
                        return (
                            <div
                                key={index}
                                style={{
                                    backgroundColor: '#262626',
                                    border: '1px solid #373737',
                                    borderRadius: '4px',
                                    padding: '16px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease'
                                }}
                                onClick={() => onViewForm && onViewForm(form)}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#2d2d2d';
                                    e.currentTarget.style.borderColor = '#404040';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = '#262626';
                                    e.currentTarget.style.borderColor = '#373737';
                                }}
                            >
                                <div style={{
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    color: '#e0e0e0',
                                    marginBottom: '8px',
                                    wordBreak: 'break-word'
                                }}>
                                    {form.url || `Form ${index + 1}`}
                                </div>
                                
                                <div style={{
                                    fontSize: '12px',
                                    color: '#a0a0a0',
                                    marginBottom: '12px',
                                    wordBreak: 'break-all'
                                }}>
                                    <a
                                        href={form.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                            color: '#60a5fa',
                                            textDecoration: 'none'
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                        onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                    >
                                        {form.url}
                                    </a>
                                </div>

                                {/* Form type badge */}
                                {form.form_type && (
                                    <div style={{ marginBottom: '12px' }}>
                                        <span style={{
                                            display: 'inline-block',
                                            backgroundColor: '#1e3a5f',
                                            color: '#60a5fa',
                                            padding: '4px 10px',
                                            borderRadius: '12px',
                                            fontSize: '11px',
                                            fontWeight: '500'
                                        }}>
                                            {formatValue(form.form_type)}
                                        </span>
                                    </div>
                                )}

                                {/* Summary stats */}
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                                    gap: '12px',
                                    marginTop: '12px'
                                }}>
                                    <div style={{
                                        backgroundColor: '#0c0c0c',
                                        border: '1px solid rgba(100, 181, 246, 0.08)',
                                        borderRadius: '6px',
                                        padding: '8px'
                                    }}>
                                        <div style={{
                                            fontSize: '10px',
                                            color: '#909090',
                                            textTransform: 'uppercase',
                                            marginBottom: '4px',
                                            fontWeight: '600',
                                            letterSpacing: '0.5px'
                                        }}>
                                            Total Fields
                                        </div>
                                        <div style={{
                                            fontSize: '13px',
                                            color: '#e0e0e0',
                                            fontWeight: '600'
                                        }}>
                                            {stats.totalFields}
                                        </div>
                                    </div>
                                    <div style={{
                                        backgroundColor: '#0c0c0c',
                                        border: '1px solid rgba(100, 181, 246, 0.08)',
                                        borderRadius: '6px',
                                        padding: '8px'
                                    }}>
                                        <div style={{
                                            fontSize: '10px',
                                            color: '#909090',
                                            textTransform: 'uppercase',
                                            marginBottom: '4px',
                                            fontWeight: '600',
                                            letterSpacing: '0.5px'
                                        }}>
                                            Required
                                        </div>
                                        <div style={{
                                            fontSize: '13px',
                                            color: '#e0e0e0',
                                            fontWeight: '600'
                                        }}>
                                            {stats.requiredFields}
                                        </div>
                                    </div>
                                    <div style={{
                                        backgroundColor: '#0c0c0c',
                                        border: '1px solid rgba(100, 181, 246, 0.08)',
                                        borderRadius: '6px',
                                        padding: '8px'
                                    }}>
                                        <div style={{
                                            fontSize: '10px',
                                            color: '#909090',
                                            textTransform: 'uppercase',
                                            marginBottom: '4px',
                                            fontWeight: '600',
                                            letterSpacing: '0.5px'
                                        }}>
                                            Visible
                                        </div>
                                        <div style={{
                                            fontSize: '13px',
                                            color: '#e0e0e0',
                                            fontWeight: '600'
                                        }}>
                                            {stats.visibleFields}
                                        </div>
                                    </div>
                                    {form.method && (
                                        <div style={{
                                            backgroundColor: '#0c0c0c',
                                            border: '1px solid rgba(100, 181, 246, 0.08)',
                                            borderRadius: '6px',
                                            padding: '8px'
                                        }}>
                                            <div style={{
                                                fontSize: '10px',
                                                color: '#909090',
                                                textTransform: 'uppercase',
                                                marginBottom: '4px',
                                                fontWeight: '600',
                                                letterSpacing: '0.5px'
                                            }}>
                                                Method
                                            </div>
                                            <div style={{
                                                fontSize: '13px',
                                                color: '#e0e0e0',
                                                fontWeight: '600',
                                                textTransform: 'uppercase'
                                            }}>
                                                {form.method}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Click hint */}
                                <div style={{
                                    marginTop: '12px',
                                    fontSize: '11px',
                                    color: '#909090',
                                    fontStyle: 'italic'
                                }}>
                                    Click to view details
                                </div>
                            </div>
                        );
                    })}
                </div>
                {hasMore && (
                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        style={{
                            marginTop: '16px',
                            padding: '8px 16px',
                            backgroundColor: '#3d3d3d',
                            border: '1px solid #505050',
                            borderRadius: '4px',
                            color: '#e0e0e0',
                            fontSize: '13px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            alignSelf: 'flex-start'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#4d4d4d';
                            e.target.style.borderColor = '#606060';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.backgroundColor = '#3d3d3d';
                            e.target.style.borderColor = '#505050';
                        }}
                    >
                        {isExpanded ? `Show Less` : `Show More (${forms.length - 3} more)`}
                    </button>
                )}
            </div>
        </div>
    );
};

export default FormsSection;

